import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { v4 as uuid } from 'uuid';

import { Button, Chip, Dropdown, Tooltip, formatNumber } from '@column/column-ui-kit';

import {
  AccountNumberCard,
  BankAccountCard,
  CopyInput,
  FileUpload,
  Loading,
  RenderFields,
  SectionHeader,
} from '~/components';
import { BankAccountRepository } from '~/repositories/BankAccountRepository';
import { AchBatchRepository } from '~/repositories/Transfer/AchBatchRepository';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Inner } from '~/styles';
import { ACHTransferBatch, BankAccountWithDetails } from '~/typings/API';

import { AchBatchHeader } from './_components/AchBatchHeader';
import { AchBatchRecordsTable, STATUS_FILTER_OPTIONS } from './_components/AchBatchRecordsTable';
import { ConfirmBatchModal } from './_components/ConfirmBatchModal';
import { hasCreatableTransfers } from './_components/recordHelpers';

const POLL_MS = 5000;

const TightDetails = styled.div`
  > div {
    padding-top: 0;
    padding-bottom: 16px;
  }
`;

const NON_TERMINAL_BATCH = new Set(['pending', 'validating', 'validated', 'processing']);

const CANCELABLE_STATUSES = new Set(['pending', 'validating', 'validated']);

const STATEMENT_GROUPING_LABEL: Record<string, string> = {
  individual: 'Individual',
  rolled_up: 'Rolled up',
};

interface Params {
  id: string;
}

export const PageTransfersAchBatchView: React.FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const platformId = useSessionStore((s) => s.currentPlatform?.id);
  const isSandbox = useSessionStore((s) => s.isSandbox);
  const formMethods = useForm();
  const queryClient = useQueryClient();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isConfirmPending, setIsConfirmPending] = useState(false);
  const [recordStatusFilter, setRecordStatusFilter] = useState<string>('');

  const batchQueryKey = ['ach-batch', platformId, isSandbox, id];

  const { data: batch, isLoading: isBatchLoading } = useQuery<ACHTransferBatch>({
    queryKey: batchQueryKey,
    queryFn: () => AchBatchRepository.get({ id }),
    enabled: !!id,
    refetchInterval: ({ state }) => (NON_TERMINAL_BATCH.has(state.data?.status ?? '') ? POLL_MS : false),
  });

  const cancelMutation = useMutation({
    mutationFn: () => AchBatchRepository.cancel(id, uuid()),
    onSuccess: () => {
      addSuccessNotification({ content: 'Import canceled' });
      queryClient.invalidateQueries({ queryKey: batchQueryKey });
    },
    onError: (error: { message?: string }) => {
      addDangerNotification({ content: error?.message ?? 'Cancel failed' });
    },
  });

  const lifecycleBusy = isConfirmPending || cancelMutation.isPending;
  const bankAccountId = batch?.importConfig?.nacha?.bankAccountId;
  const accountNumberId = batch?.importConfig?.nacha?.accountNumberId;
  const bankAccountQueryKey = [platformId, 'bank-account', bankAccountId];
  const { data: bankAccount } = useQuery<BankAccountWithDetails>({
    queryKey: bankAccountQueryKey,
    queryFn: () => BankAccountRepository.get({ id: bankAccountId ?? '' }),
    enabled: !!bankAccountId,
    gcTime: 30000,
    staleTime: 10000,
  });
  const currentBalance = bankAccount?.balances?.availableAmount;
  const currentBalanceValue = currentBalance === undefined ? '-' : formatNumber(currentBalance);

  const handleConfirmed = () => {
    queryClient.invalidateQueries({ queryKey: batchQueryKey });
    queryClient.invalidateQueries({ queryKey: bankAccountQueryKey });
  };

  return (
    <>
      <Loading isLoading={isBatchLoading} fullHeight>
        {batch && (
          <>
            {isConfirmOpen && (
              <ConfirmBatchModal
                batch={batch}
                isOpen={isConfirmOpen}
                availableBalance={currentBalanceValue}
                onClose={() => setIsConfirmOpen(false)}
                onConfirmed={handleConfirmed}
                onPendingChange={setIsConfirmPending}
              />
            )}
            <AchBatchHeader batch={batch} />
            {batch.status === 'pending' && (
              <>
                <SectionHeader text="Upload NACHA file" border />
                <Inner pt={0} px="var(--page-margin)">
                  <FileUpload<ACHTransferBatch>
                    handleUpload={(file) => AchBatchRepository.uploadFile(batch.id, file)}
                    name={`ach-batch-upload-${batch.id}`}
                    description="Upload your NACHA file"
                    renderFileName={(uploaded) => <>{uploaded.id}</>}
                    onFileUpload={() => queryClient.invalidateQueries({ queryKey: batchQueryKey })}
                  />
                </Inner>
              </>
            )}
            <SectionHeader text="Batch Details" border>
              {batch && CANCELABLE_STATUSES.has(batch.status) && (
                <Tooltip content="Discard this import. No transfers will be created." placement="bottom">
                  <Button
                    variant="secondary"
                    size="small"
                    onClick={() => cancelMutation.mutate()}
                    isLoading={cancelMutation.isPending}
                    isDisabled={lifecycleBusy}
                  >
                    Cancel batch
                  </Button>
                </Tooltip>
              )}
              {batch && hasCreatableTransfers(batch) && (
                <Tooltip content="Create transfers from the validated records in this batch." placement="bottom">
                  <Button size="small" onClick={() => setIsConfirmOpen(true)} isDisabled={lifecycleBusy}>
                    Create transfers
                  </Button>
                </Tooltip>
              )}
            </SectionHeader>
            <FormProvider {...formMethods}>
              <TightDetails>
                <RenderFields
                  sections={[
                    {
                      fields: [
                        {
                          id: 'description',
                          label: 'Description',
                          children: () => <CopyInput value={batch.description || '-'} />,
                        },
                        {
                          id: 'importType',
                          label: 'Import Type',
                          children: () => (
                            <Chip size="large">{batch.importType ? batch.importType.toUpperCase() : '-'}</Chip>
                          ),
                        },
                        ...(bankAccountId
                          ? [
                              {
                                id: 'bankAccount',
                                label: 'Bank Account',
                                children: () => (
                                  <BankAccountCard
                                    bankAccountId={bankAccountId}
                                    availableBalance={currentBalanceValue}
                                  />
                                ),
                              },
                            ]
                          : []),
                        ...(accountNumberId
                          ? [
                              {
                                id: 'accountNumber',
                                label: 'Account Number',
                                children: () => <AccountNumberCard accountNumberId={accountNumberId} />,
                              },
                            ]
                          : []),
                        {
                          id: 'statementGroupingMode',
                          label: 'Statement Grouping',
                          children: () => (
                            <Chip size="large">
                              {STATEMENT_GROUPING_LABEL[batch.statementGroupingMode] ?? batch.statementGroupingMode}
                            </Chip>
                          ),
                        },
                      ],
                    },
                  ]}
                />
              </TightDetails>
            </FormProvider>
            <SectionHeader text="Records" border>
              <Dropdown
                size="small"
                active={recordStatusFilter}
                options={STATUS_FILTER_OPTIONS}
                onChange={(value) => setRecordStatusFilter((value as string) ?? '')}
              />
            </SectionHeader>
            <Inner pt={0} px="var(--page-margin)">
              <AchBatchRecordsTable batchId={batch.id} batchStatus={batch.status} statusFilter={recordStatusFilter} />
            </Inner>
          </>
        )}
      </Loading>
    </>
  );
};
